import creditCardType from "credit-card-type";
import {
  PAYMENT_TYPES,
  APPLE_PAY_PAYMENT_TYPE,
  CREDIT_CARD_PAYMENT_TYPE,
  ELAVON_SUBPAYMENT_TYPE,
  OAPI_API_TYPE
} from "@/../app-constants";
import { ELAVON_GATEWAY_ID, FREEDOMPAY_SUBPAYMENT_TYPE, roundOffByBankApproach } from "../../utils";

const getCart = Symbol("getCart");
const getSubTotal = Symbol("getSubTotal");

const getPaymentType = (payment, isOAPI) => {
  let paymentType = payment.paymentType;
  let subpaymentType = payment.subpaymentType;

  const paymentTypes = PAYMENT_TYPES[`${paymentType}|${subpaymentType}`];

  if (isOAPI) {
    paymentType = payment?.processedPayment?.paymentType || paymentType;
    subpaymentType = payment?.processedPayment?.subpaymentType || subpaymentType;
  } else {
    paymentType = paymentTypes?.MAPIPaymentType || paymentType;
    subpaymentType = paymentTypes?.MAPISubpaymentType || subpaymentType;
  }

  return { paymentType, subpaymentType };
};

class Payment {
  constructor(card, getters, rootGetters) {
    const userData = rootGetters["user/getUserData"] || rootGetters["user/getGuestUserData"];
    const email = userData.profile ? userData.profile.email : userData.email;
    const id = rootGetters["user/getMobileUserData"]?.id;
    const isOAPI = rootGetters["venueSettings/APIType"] === OAPI_API_TYPE;
    const isElavon = rootGetters["vendor/tokenizedGatewayId"] === ELAVON_GATEWAY_ID;

    this.getCardTypes = cardNumber => {
      if (card.debitExternalData?.transactionIdentifier?.cardType) return card.debitExternalData?.transactionIdentifier?.cardType;

      const possibleCardTypes = cardNumber ? creditCardType(cardNumber) : [];
      if (possibleCardTypes.length > 0) return possibleCardTypes[0].niceType;

      return "";
    };

    this.adjustRoundedvsReal = (roundedValue, roundedTotalValue, originalTotalValue) => {
      if (Math.abs(originalTotalValue - roundedTotalValue) >= 0.001) {
        return roundOffByBankApproach(roundedValue + originalTotalValue - roundedTotalValue);
      }
      return roundedValue;
    };

    this.getPayments = () => {
      const paymentsResult = [];
      const payments = card.payments || [];
      const length = payments.length;
      const { tip, tax, fee, subtotalAmount, amount } = card;

      let roundedTotalSubtotal = 0;
      let roundedTotalTax = 0;
      let roundedTotalTip = 0;
      let roundedTotalFee = 0;
      payments.forEach((payment, index) => {
        const tenderPaymentTypes = getPaymentType(payment, isOAPI);
        const totalPaid = roundOffByBankApproach(
          payment.processedPayment?.spent || payment.appliedAmenity || payment.balance || 0
        );
        const paymentType =
          tenderPaymentTypes.paymentType || (payment.opaqueDataValue ? APPLE_PAY_PAYMENT_TYPE : CREDIT_CARD_PAYMENT_TYPE);
        const subpaymentType =
          tenderPaymentTypes.subpaymentType || (isElavon ? ELAVON_SUBPAYMENT_TYPE : FREEDOMPAY_SUBPAYMENT_TYPE);
        const adjustment = totalPaid / amount || 0;
        let roundedSubtotal = roundOffByBankApproach(subtotalAmount * adjustment);
        let roundedTip = roundOffByBankApproach(tip * adjustment);
        let roundedTax = roundOffByBankApproach(tax * adjustment);
        let roundedFee = roundOffByBankApproach(fee * adjustment);

        roundedTotalSubtotal += roundedSubtotal;
        roundedTotalTax += roundedTax;
        roundedTotalTip += roundedTip;
        roundedTotalFee += roundedFee;

        if (index === payments.length - 1) {
          roundedSubtotal = this.adjustRoundedvsReal(roundedSubtotal, roundedTotalSubtotal, subtotalAmount);
          roundedTax = this.adjustRoundedvsReal(roundedTax, roundedTotalTax, tax);
          roundedTip = this.adjustRoundedvsReal(roundedTip, roundedTotalTip, tip);
          roundedFee = this.adjustRoundedvsReal(roundedFee, roundedTotalFee, fee);
        }

        paymentsResult.push({
          ...payment,
          subpaymentType,
          paymentType,
          cardType: this.getCardTypes(payment.cardNumber),
          paymentIndex: index,
          encryptionType: "cnp",
          cardStatus: "cnp",
          subtotalAmount: roundedSubtotal,
          tip: roundedTip,
          tax: roundedTax,
          fee: roundedFee,
          amount: totalPaid
        });
      });
      return paymentsResult;
    };

    this[getCart] = (card, getters) => {
      const cart = {
        subtotalAmount: card.subtotalAmount,
        items: getters.getCartItemsForCheckout
      };

      if (getters.hasDiscount) {
        cart.subtotalAmount = getters.getDiscountedSubTotal;
        cart.promoCode = getters.getPromoInfo.promoCode;
      }

      return cart;
    };

    this[getSubTotal] = (card, getters) => {
      return getters.hasDiscount ? getters.getDiscountedSubTotal : card.subtotalAmount;
    };

    return {
      email: email,
      mobile: userData.profile ? userData.profile.phone : userData.phone?.replace(/\D/g, "") || "",
      customerName: userData.profile ? `${userData.profile.firstName} ${userData.profile.lastName}` : userData.name,
      userId: id,
      userSeatId: rootGetters["delivery/getSelectedSeat"].id || null,
      venueId: rootGetters["venue/getActiveVenue"],
      total: card.amount,
      orderSource: "sdk_client_ios",
      cart: this[getCart](card, getters),
      payments: this.getPayments(),
      tip: card.tip,
      fee: card.fee,
      tax: card.tax,
      visitorId: rootGetters["user/getVisitorId"]
    };
  }
}

export default Payment;
